/**
 * Roadmap #15 — Birdseye realtime: what is where RIGHT NOW, once.
 *
 * Birdseye places every ticket by current physical truth first
 * (`placeByPhysicalTruth`), so after placement a ticket's `bay_id` /
 * `parking_spot_id` ARE its car's current location. This module turns that
 * placed list into the ONE occupancy answer the map draws, the detail sheets
 * describe and a drop is checked against — no surface re-derives it.
 *
 * It is advisory. The move authority (`apply_vehicle_location_locked`) locks
 * the target and re-decides at commit; realtime and this projection only keep
 * the picture honest between moves.
 */

/** The fields a PLACED Birdseye ticket carries (aliased chain links, see Birdseye loadTickets). */
export interface PlacedFloorTicket {
  id: string;
  bay_id?: string | null;
  parking_spot_id?: string | null;
  location_parent_id?: string | null;
  location_rollover_id?: string | null;
}

/** The ticket that holds this car's physical truth (server: vehicle_location_holder). */
export function floorHolderId(t: PlacedFloorTicket): string {
  return t.location_parent_id ?? t.location_rollover_id ?? t.id;
}

export interface FloorOccupancy<T extends PlacedFloorTicket> {
  /** One card per spot: the ticket that holds the car, else the lowest id — never order-dependent. */
  spotOccupant: Map<string, T>;
  /** Every placed ticket in a bay (a holder and the dependents drawn where its car is). */
  bayOccupants: Map<string, T[]>;
}

const byId = (a: PlacedFloorTicket, b: PlacedFloorTicket) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export function buildFloorOccupancy<T extends PlacedFloorTicket>(tickets: readonly T[]): FloorOccupancy<T> {
  const spotOccupant = new Map<string, T>();
  const bayOccupants = new Map<string, T[]>();
  for (const t of [...tickets].sort(byId)) {
    if (t.parking_spot_id) {
      const prev = spotOccupant.get(t.parking_spot_id);
      const holdsTruth = floorHolderId(t) === t.id;
      if (!prev || (holdsTruth && floorHolderId(prev) !== prev.id)) spotOccupant.set(t.parking_spot_id, t);
    }
    if (t.bay_id) {
      const list = bayOccupants.get(t.bay_id) ?? [];
      list.push(t);
      bayOccupants.set(t.bay_id, list);
    }
  }
  return { spotOccupant, bayOccupants };
}

export type FloorDropTarget = { kind: 'BAY'; bayId: string } | { kind: 'PARKING_SPOT'; parkingSpotId: string };

/**
 * Would this drop put a car where ANOTHER car is? Returns the server's own
 * refusal wording, or null. The moving car itself (any ticket of its chain)
 * never blocks its own move.
 */
export function floorDropRefusal<T extends PlacedFloorTicket>(
  target: FloorDropTarget,
  moving: PlacedFloorTicket,
  occupancy: FloorOccupancy<T>,
): string | null {
  const mine = floorHolderId(moving);
  if (target.kind === 'PARKING_SPOT') {
    const occupant = occupancy.spotOccupant.get(target.parkingSpotId);
    return occupant && floorHolderId(occupant) !== mine ? 'Parking spot is already occupied' : null;
  }
  const occupants = occupancy.bayOccupants.get(target.bayId) ?? [];
  return occupants.some((o) => floorHolderId(o) !== mine) ? 'Bay is already occupied' : null;
}

/**
 * Latest read wins. Re-reads overlap (a realtime burst, the sender's own
 * reload, a foreground return); a slow OLDER read must never land after a
 * newer one and move a car backward. Each read takes a token when it starts
 * and may apply its result only if no later-started read has applied yet.
 */
export function createLatestReadGate() {
  let issued = 0;
  let applied = 0;
  return {
    begin(): number {
      issued += 1;
      return issued;
    },
    commit(token: number): boolean {
      if (token <= applied) return false;
      applied = token;
      return true;
    },
  };
}
